import React from "react";
import { useWizard } from "@/context/WizardContext";
import { TextInput, SelectInput, SectionHeading } from "@/components/wizard/FormKit";
import ADLQuickPattern from "@/components/wizard/ADLQuickPattern";
import { PCST_ADL_LEVELS } from "@/lib/sampleData";
import { requiresComment } from "@/lib/dhsRules";

const ADL_DEFS = [
  { key: "bathing", label: "Bathing", element: 19, pcstKey: "bathing" },
  { key: "dressing", label: "Dressing (general)", element: 20, pcstKey: "dressing" },
  { key: "eating", label: "Eating", element: 23, pcstKey: "eating" },
  { key: "mobility", label: "Mobility", element: 24, pcstKey: "mobility" },
  { key: "toileting", label: "Toileting", element: 25, pcstKey: "toileting" },
  { key: "transferring", label: "Transferring", element: 26, pcstKey: "transferring" },
];

const SCORE_OPTIONS = [
  { value: "0", label: "0 — Independent" },
  { value: "1", label: "1 — Supervision" },
  { value: "2", label: "2 — Limited assist" },
  { value: "3", label: "3 — Extensive assist" },
  { value: "4", label: "4 — Total dependence" },
];

const IADL_DEFS = [
  { key: "meal_prep", label: "Meal Preparation" },
  { key: "housekeeping", label: "Light Housekeeping" },
  { key: "laundry", label: "Laundry" },
  { key: "shopping", label: "Shopping" },
  { key: "medication_mgmt", label: "Medication Management" },
  { key: "money_mgmt", label: "Money Management" },
  { key: "phone_use", label: "Telephone Use" },
  { key: "transportation", label: "Transportation" },
];

export default function Step2ADL() {
  const { data, update } = useWizard();

  // Merge pattern values into existing ADL rows (preserve notes etc.)
  const applyPattern = (patternValues) => {
    const patch = {};
    Object.entries(patternValues).forEach(([k, v]) => {
      patch[k] = { ...(data.adl[k] || {}), ...v };
    });
    update("adl", patch);
  };

  return (
    <div className="space-y-10">
      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 2 · Section E" title="ADL Assessment (6 Activities of Daily Living)"
          description="Score each ADL 0-4 and describe frequency, type of assistance, and notes." />
        <div className="mb-6">
          <ADLQuickPattern onApply={applyPattern} />
        </div>
        <div className="space-y-6">
          {ADL_DEFS.map((a) => {
            const row = data.adl[a.key] || {};
            const pcstOptions = PCST_ADL_LEVELS[a.pcstKey] || [];
            const needsComment = requiresComment(row.pcst_level, a.key);
            const commentMissing = needsComment && !(row.notes || "").trim();
            return (
              <div key={a.key} className="rounded-xl border border-[#D6E3EF] p-5 bg-[#F0F6FC]" data-testid={`adl-row-${a.key}`}>
                <div className="flex flex-wrap items-baseline justify-between mb-4 gap-3">
                  <h3 className="font-display text-lg font-semibold text-[#0B1E3A]">
                    Element {a.element}. {a.label}
                  </h3>
                  <span className="text-[11px] uppercase tracking-[0.14em] text-[#3A5275]">PCST F-11133</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <SelectInput label="PCST Level (F-11133)" options={pcstOptions}
                    value={row.pcst_level} onChange={(v) => update("adl", { [a.key]: { ...row, pcst_level: v, days_per_week: row.days_per_week || "7" } })}
                    testid={`adl-${a.key}-pcst-level`} />
                  <TextInput label="Days per Week" placeholder="0–7"
                    value={row.days_per_week} onChange={(v) => update("adl", { [a.key]: { ...row, days_per_week: v } })}
                    testid={`adl-${a.key}-days`} />
                  <TextInput
                    label={needsComment ? "Notes / Comments (REQUIRED for Level C+)" : "Notes / Comments (optional)"}
                    placeholder={needsComment ? "Describe functional impairment, PCW hands-on actions, and any DME used…" : "equipment, safety…"}
                    value={row.notes} onChange={(v) => update("adl", { [a.key]: { ...row, notes: v } })}
                    testid={`adl-${a.key}-notes`} />
                </div>
                {commentMissing && (
                  <div
                    className="mt-3 text-[12.5px] rounded-md border border-[#e2b6a9] bg-[#fbeae5] text-[#8a2e1a] px-3 py-2"
                    data-testid={`adl-${a.key}-dhs-warning`}
                  >
                    <strong>DHS Rule:</strong> Level <code>{(row.pcst_level || "").toUpperCase()}</code> requires a
                    comment describing the member's functional impairment, PCW hands-on intervention, and any DME used.
                    Submissions without this comment will fail the RN checklist.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 2 · Section F" title="IADL Assessment (Instrumental ADLs)"
          description="Tasks supported by the PCW beyond core ADL care." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {IADL_DEFS.map((i) => {
            const row = data.iadl[i.key] || {};
            return (
              <div key={i.key} className="rounded-xl border border-[#D6E3EF] p-4 bg-white" data-testid={`iadl-row-${i.key}`}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-[#0B1E3A] text-[15px]">{i.label}</h4>
                  <span className="text-[11px] uppercase tracking-[0.14em] text-[#3A5275]">IADL</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <SelectInput label="Score" options={SCORE_OPTIONS}
                    value={row.score} onChange={(v) => update("iadl", { [i.key]: { ...row, score: v } })}
                    testid={`iadl-${i.key}-score`} />
                  <TextInput label="Notes"
                    value={row.notes} onChange={(v) => update("iadl", { [i.key]: { ...row, notes: v } })}
                    testid={`iadl-${i.key}-notes`} />
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
